/**
 * Copyright Open Logistics Foundation
 *
 * Licensed under the Open Logistics Foundation License 1.3.
 * For details on the licensing terms, see the LICENSE file.
 * SPDX-License-Identifier: OLFL-1.3
 */

import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from '@app/shared/material/material.module';
import { DragDropDirective } from '@app/shared/directives/drag-drop.directive';
import { ToastService } from '@app/core/services/toast.service';
import { EcmrDocumentService } from '@app/core/services/ecmr-services/ecmr-document.service';
import { EcmrDocument } from '@app/shared/models/ecmr/EcmrDocument';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
const ALLOWED_MIME_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];

@Component({
    selector: 'app-ecmr-documents',
    templateUrl: './ecmr-documents.component.html',
    styleUrl: './ecmr-documents.component.scss',
    imports: [CommonModule, MaterialModule, TranslateModule, DragDropDirective],
})
export class EcmrDocumentsComponent implements OnChanges {
    @Input() ecmrId: string;
    @Input() userToken: string | null;
    @Input() tan: string | null;
    @Input() disabled: boolean = false;

    documents: EcmrDocument[] = [];
    loading: boolean = false;
    uploading: boolean = false;
    uploadProgress: number = 0;

    constructor(
        private documentService: EcmrDocumentService,
        private toastr: ToastService,
        private translateService: TranslateService
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ecmrId'] && this.ecmrId) {
            this.loadDocuments();
        }
    }

    loadDocuments(): void {
        this.loading = true;
        this.documentService
            .listDocuments(this.ecmrId, this.userToken, this.tan)
            .subscribe({
                next: docs => {
                    this.documents = docs;
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                },
            });
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.uploadFile(input.files[0]);
            input.value = '';
        }
    }

    onFilesDropped(files: FileList): void {
        if (files.length > 0) {
            this.uploadFile(files[0]);
        }
    }

    private validateFile(file: File): boolean {
        if (file.size > MAX_FILE_SIZE) {
            this.toastr.error(
                this.translateService.instant(
                    'ECMR.ecmr_editor.documents.error_file_too_large'
                )
            );
            return false;
        }

        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
            this.toastr.error(
                this.translateService.instant(
                    'ECMR.ecmr_editor.documents.error_invalid_type'
                )
            );
            return false;
        }

        return true;
    }

    private uploadFile(file: File): void {
        if (!this.validateFile(file)) {
            return;
        }

        this.uploading = true;
        this.uploadProgress = 0;

        this.documentService
            .uploadDocument(this.ecmrId, file, this.userToken, this.tan)
            .subscribe({
                next: event => {
                    if (event.type === HttpEventType.UploadProgress) {
                        this.uploadProgress = event.total
                            ? Math.round((100 * event.loaded) / event.total)
                            : 0;
                    } else if (event.type === HttpEventType.Response) {
                        this.uploading = false;
                        this.toastr.success(
                            this.translateService.instant(
                                'ECMR.ecmr_editor.documents.upload_success'
                            )
                        );
                        this.loadDocuments();
                    }
                },
                error: (err: HttpErrorResponse) => {
                    this.uploading = false;
                    if (err.status === 422) {
                        this.toastr.error(
                            this.translateService.instant(
                                'ECMR.ecmr_editor.documents.error_virus_detected'
                            )
                        );
                    } else if (err.status === 429) {
                        this.toastr.warning(
                            this.translateService.instant(
                                'ECMR.ecmr_editor.documents.error_rate_limit'
                            )
                        );
                    } else {
                        this.toastr.error(
                            this.translateService.instant(
                                'ECMR.ecmr_editor.documents.error_upload_failed'
                            )
                        );
                    }
                },
            });
    }

    downloadDocument(doc: EcmrDocument): void {
        this.documentService
            .downloadDocument(doc.id, this.userToken, this.tan)
            .subscribe({
                next: response => {
                    if (response.body) {
                        const blob = new Blob([response.body], {
                            type: 'application/octet-stream',
                        });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = doc.fileName;
                        link.click();
                        URL.revokeObjectURL(url);
                    }
                },
                error: () => {
                    this.toastr.error(
                        this.translateService.instant(
                            'ECMR.ecmr_editor.documents.error_download_failed'
                        )
                    );
                },
            });
    }

    deleteDocument(doc: EcmrDocument): void {
        if (
            !confirm(
                this.translateService.instant(
                    'ECMR.ecmr_editor.documents.delete_confirm'
                )
            )
        ) {
            return;
        }

        this.documentService
            .deleteDocument(doc.id, this.userToken, this.tan)
            .subscribe({
                next: () => {
                    this.toastr.success(
                        this.translateService.instant(
                            'ECMR.ecmr_editor.documents.delete_success'
                        )
                    );
                    this.loadDocuments();
                },
                error: () => {
                    this.toastr.error(
                        this.translateService.instant(
                            'ECMR.ecmr_editor.documents.error_delete_failed'
                        )
                    );
                },
            });
    }

    getFileIcon(mimeType: string): string {
        if (mimeType === 'application/pdf') {
            return 'picture_as_pdf';
        }
        return 'image';
    }

    formatFileSize(bytes: number): string {
        if (bytes < 1024) {
            return bytes + ' B';
        } else if (bytes < 1024 * 1024) {
            return (bytes / 1024).toFixed(1) + ' KB';
        }
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }
}
